/*
 * KeyValueList: labelled facts (server details, billing details). `mono` values render LTR in Plex Mono;
 * `copy` adds a copy button for that value.
 *   <KeyValueList items={[
 *     { label: t('servers:hostname'), value: s.hostname, mono: true, copy: s.hostname },
 *     { label: t('servers:plan'), value: <Badge tone="brand">Pro</Badge> },
 *   ]} columns={2} />
 */
import { Check, Copy } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { useCopy } from '@/lib/useCopy'

export type KeyValueItem = {
  label: ReactNode
  value: ReactNode
  mono?: boolean
  copy?: string
  hint?: ReactNode
}

function CopyInline({ text }: { text: string }) {
  const { t } = useTranslation('common')
  const [copied, copy] = useCopy()
  return (
    <button
      type="button"
      onClick={() => copy(text)}
      aria-label={copied ? t('actions.copied') : t('actions.copy')}
      className="grid size-7 shrink-0 place-items-center rounded-md text-subtle opacity-70 transition hover:bg-control hover:text-ink hover:opacity-100 touch:size-10"
    >
      {copied ? (
        <Check className="size-3.5 text-teal-600" aria-hidden="true" />
      ) : (
        <Copy className="size-3.5" aria-hidden="true" />
      )}
    </button>
  )
}

export function KeyValueList({
  items,
  columns = 1,
  className,
}: {
  items: KeyValueItem[]
  columns?: 1 | 2 | 3
  className?: string
}) {
  const grid = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3' }[columns]
  return (
    <dl className={cn('grid gap-x-8', grid, className)}>
      {items.map((item, i) => (
        <div
          key={i}
          className="flex flex-col gap-1 border-b border-hairline py-3 last:border-0 sm:[&:nth-last-child(-n+1)]:border-0"
        >
          <dt className="text-13 text-muted">{item.label}</dt>
          <dd className="flex min-w-0 items-center gap-1 text-14 text-ink">
            <span
              className={cn('min-w-0 truncate', item.mono && 'font-mono text-13')}
              dir={item.mono ? 'ltr' : undefined}
            >
              {item.value ?? '-'}
            </span>
            {item.copy ? <CopyInline text={item.copy} /> : null}
          </dd>
          {item.hint ? <dd className="text-12 text-subtle">{item.hint}</dd> : null}
        </div>
      ))}
    </dl>
  )
}
